import { Component, EventEmitter, Input, Output, OnInit, ViewChild, ElementRef, AfterViewInit, TemplateRef, SimpleChanges, OnChanges, } from '@angular/core';
import { Observable, Subject, debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs';
import { DataService } from '../../services/data.service';
import { RouterService } from '../../services/router.service';
import { environment } from 'src/environments/environment';
import { FormControl } from '@angular/forms';
import { AutocompleteRequest } from 'src/app/models/requests/portalAutocompleteRequest';
import { ActivatedRoute } from '@angular/router';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-search-field',
  templateUrl: './search-field.component.html',
  styleUrls: ['./search-field.component.scss', './search-field.component.en.scss'],
})
export class SearchFieldComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() mainCatalogId!: number;
  @Input() placeholder: string = '';
  @Input() apiEndpointUrl: string = '';
  @Input() autocompleteNoResultsText: string | null = null;
  @Input() isMobile: boolean = false;
  @Input() childNodeId: string | unknown = null;
  @Input() useCustomSubmitHandler: boolean = false;
  @Output() searchQueryChange = new EventEmitter<string>();
  @Output() onSubmit = new EventEmitter<any>();
  @Output() onSubmitObj = new EventEmitter<any>();
  @Output() openCloseMobilePopup = new EventEmitter<boolean>();

  searchInputControl = new FormControl<string | any>('');
  filteredOptions!: Observable<any[]>;
  searchTermObj = new Subject<string>();
  searchQuery: string = '';
  showOpenMobileSearch = false;
  debounceDisabled: boolean = false;

  @ViewChild('searchForm') searchForm!: ElementRef;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger?: MatAutocompleteTrigger;

  private debounceTimeout: number = 300;

  constructor(
    private dataService: DataService,
    private routerService: RouterService,
    private route: ActivatedRoute
  ) {
    this.searchTermObj.pipe(debounceTime(this.debounceTimeout)).subscribe(term => {
      if (!this.debounceDisabled) {
        this.performSearch(term);
      }
      this.debounceDisabled = false; // Reset the flag
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes['isMobile']) {
      this.closeSearchMobilePopup();
    }
  }

  ngAfterViewInit(): void {
    window.addEventListener('scroll', () => {
      if(this.autocompleteTrigger && this.autocompleteTrigger.panelOpen) {
         this.autocompleteTrigger.updatePosition();
      }
    }, true);
  }

  ngOnInit(): void {
    let term = this.route.snapshot.queryParams['term'];
    if (term) {
      this.searchQuery = term;
      this.searchInputControl.setValue(term);
    }
    
    this.filteredOptions = this.searchInputControl.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      startWith(this.searchQuery),
      switchMap((term) => this.filter(term))
    );
  }
  
  onInputChange(event: any) {
    this.searchTermObj.next(event.target.value);
  }

  performSearch(term: string, isFilter: boolean = false) {
    this.searchQuery = term;
    this.searchInputControl.setValue(term);
    if(!isFilter) {
      this.searchQueryChange.emit(this.searchQuery);
    }
  }

  onAutocompleteSelect(suggestion: any) {
    //for empty result click
    if(!suggestion) {
      this.searchInputControl.setValue(this.searchQuery);
      return;
    }
    //use custon handler if item value is item and the useCustomSubmitHandler is true
    if(this.useCustomSubmitHandler && suggestion.isItemValue) {
      this.performSearch(suggestion.key, true);
      this.closeSearchMobilePopup();
      this.autocompleteTrigger?.closePanel();
      this.onSubmit.emit(suggestion);
      return;
    }
    //use custon handler if item is not filter and the useCustomSubmitHandler is true
    if(this.useCustomSubmitHandler && !suggestion.isFilterValue) {
      this.searchInputControl.reset();
      this.closeSearchMobilePopup();
      this.autocompleteTrigger?.closePanel();
      this.onSubmit.emit(suggestion);
      return;
    }

    //if suggestion is not filter and have link
    if (suggestion.value && !suggestion.isFilterValue) {
      this.searchInputControl.setValue(suggestion.key);
      window.location.href = suggestion.value;
      return;
    }

    //else if filter
    this.performSearch(suggestion.key, true);
    this.closeSearchMobilePopup();
    this.autocompleteTrigger?.closePanel();
    this.onSubmit.emit(suggestion.value);
    this.onSubmitObj.emit(suggestion);
  }

  onSearchSubmit(event: any) {
    event?.preventDefault();
    this.closeSearchMobilePopup();
    const term = this.searchInputControl.value;
    this.debounceDisabled = true; // Disable debounce temporarily
    this.performSearch(term)
    this.autocompleteTrigger?.closePanel();
    this.onSubmit.emit();
  }

  clearInput() {
    this.closeSearchMobilePopup();
    this.debounceDisabled = true; // Disable debounce temporarily
    this.searchInputControl.reset();
    this.performSearch('');
    this.autocompleteTrigger?.closePanel();
    this.onSubmit.emit();
    setTimeout(() => { this.debounceDisabled = false }, this.debounceTimeout)
  }

  clearInputText() {
    this.searchInputControl.reset();
  }

  scrollToSearchForm() {
    this.searchForm.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  private filter(term: string) {
    const request: AutocompleteRequest = {
      nodeId: this.mainCatalogId,
      childNodeId: this.childNodeId ? parseInt(this.childNodeId.toString()) : undefined,
      cultureCode: this.dataService.getCultureCode(),
      term: term,
      pageUrl: this.routerService.getCurrentComponentRoute(),
    };
    return this.dataService
      .getAutoCompleteResult(
        environment.apiUrl + this.apiEndpointUrl,
        request
      ).pipe(map((data) => JSON.parse(data)));
  }

  openSearchMobilePopup() {
    this.showOpenMobileSearch = true;
    this.openCloseMobilePopup.emit(false);
  }

  closeSearchMobilePopup() {
    this.showOpenMobileSearch = false;
    this.openCloseMobilePopup.emit(true);
  }
}
